import React, { useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import Select, { MultiValue } from "react-select";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  ClassicEditor,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Link,
  List,
  Indent,
  IndentBlock,
  BlockQuote,
  HorizontalLine,
  Heading,
  Font,
  FontFamily,
  FontSize,
  FontColor,
  FontBackgroundColor,
  Undo,
  Essentials,
  Paragraph,
  RemoveFormat,
} from "ckeditor5";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm, SubmitHandler } from "react-hook-form";
import "ckeditor5/ckeditor5.css";

import AuthLayout from "../../../components/layouts/auth";
import ApiRequest from "../../../lib/ApiCall";
import InputField from "../../../components/form/input-field";
import { EmptyFormInput } from "../../../lib/helper-support";
import { ErrorFlash, SuccessFlash } from "../../../lib/flash-notification";
import SpinnerLoader from "../../../components/loader/spinner-loader";
import { ChevronLeft } from "lucide-react";

/* ---------------- TYPES ---------------- */

type SendToType = "all" | "selected";
type ContactType = "all" | "primary";

interface MemberOption {
  label: string;
  value: string;
}

interface FormValues {
  subject: string;
  message: string;
}

interface ApiResponse {
  status: string;
  message?: string;
  data?: {
    items: Array<{
      uuid: string;
      nameInstitution: string;
    }>;
  };
}

/* ---------------- COMPONENT ---------------- */

const PushMessage: React.FC = () => {
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [sendTo, setSendTo] = useState<SendToType>("all");
  const [contactType, setContactType] = useState<ContactType>("all");

  const [memberOptions, setMemberOptions] = useState<MemberOption[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<MemberOption[]>([]);
  const [loadingMembers, setLoadingMembers] = useState<boolean>(false);

  const editorRef = useRef<any>(null);

  const formSchema: yup.ObjectSchema<FormValues> = yup.object({
    subject: yup.string().required("Subject is required"),
    message: yup.string().required("Message is required"),
  });

  const {
    setValue,
    setError,
    formState: { errors },
    handleSubmit,
    getValues,
    reset,
  } = useForm<FormValues>({
    resolver: yupResolver(formSchema),
  });

  /* ---------------- FETCH MEMBERS ---------------- */

  const fetchMembers = async (inputValue: string): Promise<void> => {
    if (!inputValue) return;

    setLoadingMembers(true);

    const res: ApiResponse = await ApiRequest({
      endpoint: `member?search=${inputValue}`,
      method: "GET",
    });

    setLoadingMembers(false);

    if (res.status === "200" && res.data) {
      const options: MemberOption[] = res.data.items.map((user) => ({
        label: user.nameInstitution,
        value: user.uuid,
      }));

      setMemberOptions(options);
    }
  };

  /* ---------------- SUBMIT ---------------- */

  const sendNotification: SubmitHandler<FormValues> = async (data) => {
    setSubmitLoading(true);

    const payload = {
      ...data,
      send_to: sendTo,
      contact_type: contactType,
      members:
        sendTo === "selected"
          ? selectedMembers.map((m) => m.value)
          : [],
    };

    const request: ApiResponse = await ApiRequest({
      endpoint: "notify-members-new",
      method: "POST",
      payload,
    });

    setSubmitLoading(false);

    if (request.status !== "200") {
      ErrorFlash(request.message || "Error sending notification");
      return;
    }

    SuccessFlash("Notification sent successfully");

    setTimeout(() => {
      reset(EmptyFormInput(formSchema));
      setSelectedMembers([]);
      setContactType("all");

      if (editorRef.current) {
        editorRef.current.setData("");
      }
    }, 2000);
  };

  /* ---------------- STYLES ---------------- */

  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      backgroundColor: "#fff",
      borderColor: "#d1d5db",
    }),
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: "#fff",
      zIndex: 9999,
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isFocused ? "#f3f4f6" : "#fff",
      color: "#000",
    }),
    multiValue: (provided: any) => ({
      ...provided,
      backgroundColor: "#e5e7eb",
    }),
  };

  /* ---------------- UI ---------------- */

  return (
    <AuthLayout>
      <form onSubmit={handleSubmit(sendNotification)}>
        <div className="flex items-center mb-5 space-x-2">
          <RouterLink to={"/message"}>
            <ChevronLeft />
          </RouterLink>
          <h1 className="page-title">Send Notification</h1>
        </div>

        {/* SUBJECT */}
        <div className="mb-5">
          <InputField
            error={errors.subject?.message}
            onChangeInput={(e: React.ChangeEvent<HTMLInputElement>) => {
              setValue("subject", e.target.value);
              setError("subject", { message: "" });
            }}
            required
            value={getValues("subject")}
            label="Subject"
          />
        </div>

        {/* SEND TO */}
        <div className="mb-5">
          <label className="block mb-2 font-medium">Send To</label>

          <div className="flex gap-4">
            <label>
              <input
                type="radio"
                checked={sendTo === "all"}
                onChange={() => setSendTo("all")}
              />{" "}
              All Members
            </label>

            <label>
              <input
                type="radio"
                checked={sendTo === "selected"}
                onChange={() => setSendTo("selected")}
              />{" "}
              Selected Members
            </label>
          </div>
        </div>


        {/* SELECT MEMBERS */}
        {sendTo === "selected" && (
          <div className="mb-5">
            <label className="block mb-2 font-medium">
              Select Members
            </label>

            <Select<MemberOption, true>
              isMulti required={true}
              options={memberOptions}
              value={selectedMembers}
              onChange={(value: MultiValue<MemberOption>) =>
                setSelectedMembers([...value])
              }
              styles={customStyles}
              onInputChange={(inputValue: string) => {
                fetchMembers(inputValue);
                return inputValue;
              }}
              isLoading={loadingMembers}
              placeholder="Search members..."
              noOptionsMessage={() => "Type to search users"}
            />
          </div>
        )}

        {/* CONTACT TYPE */}
        <div className="mb-5">
          <label className="block mb-2 font-medium">Contact Type</label>

          <div className="flex gap-4">
            <label>
              <input
                type="radio"
                checked={contactType === "all"}
                onChange={() => setContactType("all")}
              />{" "}
              All Contacts
            </label>

            <label>
              <input
                type="radio"
                checked={contactType === "primary"}
                onChange={() => setContactType("primary")}
              />{" "}
              Primary Only
            </label>
          </div>
        </div>


        {/* MESSAGE */}
        <div className="ck-editor-wrapper">
          <label className="block mb-2 font-medium">Message <small>(use this [primary_contact_email] to add primary contact email to your message.)</small></label>


          <CKEditor
            editor={ClassicEditor}
            config={{
              licenseKey: "GPL",
              plugins: [
                Essentials,
                Paragraph,
                Bold,
                Italic,
                Underline,
                Strikethrough,
                Link,
                List,
                Indent,
                IndentBlock,
                BlockQuote,
                HorizontalLine,
                Heading,
                Font,
                FontFamily,
                FontSize,
                FontColor,
                FontBackgroundColor,
                Undo,
                RemoveFormat,
              ],
              toolbar: [
                "undo",
                "redo",
                "|",
                "heading",
                "|",
                "fontFamily",
                "fontSize",
                "fontColor",
                "fontBackgroundColor",
                "|",
                "bold",
                "italic",
                "underline",
                "strikethrough",
                "|",
                "link",
                "blockQuote",
                "horizontalLine",
                "|",
                "bulletedList",
                "numberedList",
                "|",
                "indent",
                "outdent",
                "|",
                "removeFormat",
              ],
            }}
            onReady={(editor: any) => {
              editorRef.current = editor;
            }}
            data={getValues("message") || ""}
            onChange={() => {
              setValue("message", editorRef.current?.getData() || "");
              setError("message", { message: "" });
            }}
          />
        </div>

        {errors.message && (
          <p className="my-1 text-sm text-red-500">
            {errors.message.message}
          </p>
        )}

        {/* SUBMIT */}
        <button
          disabled={submitLoading}
          className={`mt-5 pryBtn w-fit ${submitLoading ? "opacity-50" : ""}`}
        >
          Send Notification {submitLoading && <SpinnerLoader />}
        </button>
      </form>
    </AuthLayout>
  );
};

export default PushMessage;